import { EllipsisVerticalIcon, TrashIcon } from '@heroicons/react/24/outline';
import { LinearProgress } from '@mui/joy';
import Chip from '@mui/joy/Chip';
import {
  ColumnDef,
  PaginationState,
  Row,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { ReactNode, useState } from 'react';

import { useAuth } from '@/hooks/useAuth/useAuth';
import { useFeedbackSourceUser } from '@/hooks/useFeedbackSourceUser';
import { apiFeedbackPath, apiRunsPath } from '@/utils/constants';

import { DEFAULT_10_PAGINATION_MODEL } from '../../constants/dataGridConstants';
import { useFeedbacks } from '../../hooks/useSwr';
import { FeedbackSchema, GetFeedbackQueryParams } from '../../types/schema';
import { DataGrid, DataGridPagination } from '../DataGrid';
import { useDataGridSizingLocalStorage } from '../DataGrid.utils';
import { DeleteModal } from '../Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { ErrorBanner } from '../ErrorBanner';
import {
  FeedbackChip,
  SingleFeedbackChipWithSourceRunAction,
} from '../FeedbackChips';
import { ProfileCircle } from '../ProfileCircle';
import { TableCellTextOverflow } from '../Table';
import { TextOverflowTooltip } from '../TextOverflowTooltip';
import { LongContentTooltip } from './components/LongContentTooltip';
import { SourceRunAction, getRunId } from './components/SourceRunAction';

const columnHelper = createColumnHelper<FeedbackSchema>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMNS_DEF_FIRST: ColumnDef<FeedbackSchema, any>[] = [
  columnHelper.accessor('feedback_source.type', {
    header: 'Source',
    cell: ({ getValue }) => (
      <Chip
        variant="outlined"
        color="neutral"
        size="sm"
        sx={{
          fontWeight: 'bold',
          textTransform: 'uppercase',
          userSelect: 'none',
          fontSize: 10,
        }}
      >
        {getValue()}
      </Chip>
    ),
    enableSorting: false,
    maxSize: 100,
  }),
  columnHelper.display({
    id: 'sourceRunAction',
    cell: ({ row }) => {
      return <SourceRunAction feedback={row.original} />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 40,
  }),
  columnHelper.accessor('key', {
    header: 'Key',
    cell: ({ row, getValue }) => {
      const runId = getRunId(row.original);
      return runId ? (
        <SingleFeedbackChipWithSourceRunAction
          feedbackKey={getValue()}
          feedbackSourceRunId={row.original.run_id}
          errors={row.original.extra?.error ?? 0}
          showErrorCount={row.original.extra?.error ?? false}
        />
      ) : (
        <FeedbackChip
          feedback_key={getValue()}
          allowTruncation
          errors={row.original.extra?.error ?? 0}
          showErrorCount={row.original.extra?.error ?? false}
        />
      );
    },
    enableSorting: false,
    minSize: 150,
  }),

  columnHelper.accessor('score', {
    header: 'Score',
    enableSorting: false,
  }),
  columnHelper.accessor('value', {
    header: 'Value',
    enableSorting: false,
    cell: ({ getValue }) => <LongContentTooltip value={getValue()} />,
  }),
  columnHelper.accessor('comment', {
    header: 'Comment',
    enableSorting: false,
    cell: ({ getValue }) => <LongContentTooltip value={getValue()} />,
  }),
  columnHelper.accessor('correction', {
    header: 'Correction',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return <LongContentTooltip value={value} displayAsJSON />;
    },
  }),
  columnHelper.accessor('feedback_source.metadata', {
    header: 'Source Info',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value =
        typeof getValue() !== 'undefined'
          ? JSON.parse(JSON.stringify(getValue()))
          : undefined;
      if (!value) {
        return null;
      }

      delete value['__run'];

      if (Object.keys(value).length === 0) return null;
      return <LongContentTooltip value={value} displayAsJSON />;
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Created At',
    cell: ({ row }) => (
      <LongContentTooltip
        value={new Date(row.original.created_at + 'Z').toLocaleString()}
      />
    ),
    enableSorting: false,
  }),
];

const COLUMNS_DEF_LAST: ColumnDef<FeedbackSchema, any>[] = [
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (id: string) => void;
        };
      };

      return (
        <TableCellTextOverflow className="justify-end">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                onClick={(e) => e.stopPropagation()}
                className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  onClick={() => actions.handleDelete(row.original.id)}
                  className="flex items-center gap-2"
                >
                  <TrashIcon className="h-5 w-5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCellTextOverflow>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 80,
  }),
];

const EMPTY_TABLE = [];

const CreatedByCell = ({ row }: { row: Row<FeedbackSchema> }) => {
  const { isActiveUser, feedbackUser } = useFeedbackSourceUser(
    row.original.feedback_source?.user_id
  );
  if (row.original.feedback_source?.user_id == undefined) {
    return null;
  }
  const username =
    feedbackUser?.full_name ??
    row.original.feedback_source?.user_name ??
    'User';
  return username ? (
    <div className="flex h-full items-center justify-start gap-2">
      <div className="flex-none">
        <ProfileCircle active={isActiveUser} name={username} />
      </div>
      <TextOverflowTooltip>{username}</TextOverflowTooltip>
    </div>
  ) : null;
};

const InnerFeedbackTable = (props: {
  variant: 'public' | 'private';
  pagination: PaginationState;
  setPagination: (
    updater: ((old: PaginationState) => PaginationState) | PaginationState
  ) => void;
  emptyState?: ReactNode;
  feedbacks: ReturnType<typeof useFeedbacks>;
}) => {
  // columns and actions
  'use no memo';
  const [deleteOpen, setDeleteOpen] = useState<string | null>(null);

  const { needsAuth } = useAuth();

  const userColumn = needsAuth
    ? [
        columnHelper.accessor('feedback_source.user_name', {
          header: 'Created by',
          cell: ({ row }) => <CreatedByCell row={row} />,
          enableSorting: false,
        }),
      ]
    : [];

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('feedbacks');

  const table = useReactTable({
    columns: [...COLUMNS_DEF_FIRST, ...userColumn, ...COLUMNS_DEF_LAST],
    data: props.feedbacks.data ?? EMPTY_TABLE,

    state: {
      pagination: props.pagination,
      columnVisibility: {
        actions: props.variant === 'private',
      },
      columnSizing,
    },
    meta: {
      actions: { handleDelete: setDeleteOpen },
    },
    onPaginationChange: props.setPagination,
    onColumnSizingChange: setColumnSizing,

    getRowId: (row) => row.id,

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),

    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    columnResizeMode: 'onChange',

    pageCount: Math.ceil(
      +(props.feedbacks.data?.headers?.['x-pagination-total'] || 0) /
        props.pagination.pageSize
    ),
  });

  if (props.feedbacks.error) {
    return <ErrorBanner>{props.feedbacks.error.message}</ErrorBanner>;
  }

  if (props.feedbacks.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (
    !props.feedbacks.data ||
    (!props.feedbacks.data.length && props.emptyState)
  ) {
    return <>{props.emptyState}</>;
  }

  return (
    <>
      <DataGrid
        table={table}
        isLoading={props.feedbacks.isLoading || props.feedbacks.isValidating}
        emptyState={props.emptyState}
      />

      <DataGridPagination table={table} />

      <DeleteModal
        endpoint={apiFeedbackPath}
        id={deleteOpen as string}
        isOpen={!!deleteOpen}
        doClose={() => setDeleteOpen(null)}
        invalidationPrefixes={[apiRunsPath]}
      />
    </>
  );
};

export const FeedbackTable = (props: {
  shareToken?: string;
  datasetShareToken?: string;
  filter: GetFeedbackQueryParams;
  emptyState?: React.ReactNode;
}) => {
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_10_PAGINATION_MODEL
  );
  const feedbacks = useFeedbacks(
    {
      ...props.filter,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      include_user_names: true,
    },
    {
      runShareToken: props.shareToken,
      datasetShareToken: props.datasetShareToken,
    }
  );

  return (
    <InnerFeedbackTable
      {...props}
      variant={
        props.datasetShareToken || props.shareToken ? 'public' : 'private'
      }
      pagination={pagination}
      setPagination={setPagination}
      feedbacks={feedbacks}
    />
  );
};
